import { useState, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { useAuthStore } from '@/stores/authStore'
import { buildTrendData, EMPTY_PARTNER_FORM } from '@/pages/partnersPageConstants'
import type { PartnerFormState, PartnerView } from '@/components/Partners/partnerView.types'

const EMPTY_FORM: PartnerFormState = { ...EMPTY_PARTNER_FORM, responsavelUserId: '' }

export function usePartnersPage() {
  const role = useAuthStore((s) => s.user?.role)
  const canAssignCommercial = role?.toLowerCase() === 'admin' || role?.toLowerCase() === 'manager'
  const canApprovePortalAccess = canAssignCommercial
  const [comerciais, setComerciais] = useState<{ id: string; name: string }[]>([])
  const [partners, setPartners] = useState<PartnerView[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showFormModal, setShowFormModal] = useState(false)
  const [formData, setFormData] = useState<PartnerFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<PartnerView | null>(null)
  const [pyramidVersion, setPyramidVersion] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    const fetchPartners = async () => {
      try {
        const resp = await api.get('/parceiros', { signal: controller.signal })
        setPartners(resp.data.data ?? [])
      } catch (err: unknown) {
        if (!controller.signal.aborted) {
          setPartners([])
          toast.error(getFriendlyError(err, 'Erro ao carregar parceiros'))
        }
      } finally {
        setLoading(false)
      }
    }
    fetchPartners()
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!canAssignCommercial) return
    const controller = new AbortController()
    api.get('/parceiros/comerciais', { signal: controller.signal })
      .then((resp) => setComerciais(resp.data.data ?? []))
      .catch(() => setComerciais([]))
    return () => controller.abort()
  }, [canAssignCommercial])

  const filtered = useMemo(() => {
    return partners
      .map((partner, index) => ({ partner, index }))
      .filter(({ partner }) =>
        partner.nome.toLowerCase().includes(search.toLowerCase()) ||
        partner.email?.toLowerCase().includes(search.toLowerCase()) ||
        partner.empresa?.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        const aPending = a.partner.portalUserId ? 0 : 1
        const bPending = b.partner.portalUserId ? 0 : 1
        return bPending - aPending || a.index - b.index
      })
      .map(({ partner }) => partner)
  }, [partners, search])

  const sparklineData = useMemo(() => buildTrendData(partners.length), [partners.length])
  const sparklineToPay = useMemo(() => {
    const total = partners.reduce((acc, p) => acc + (p.stats?.totalToPay ?? 0), 0)
    return buildTrendData(total)
  }, [partners])
  const sparklinePaid = useMemo(() => {
    const total = partners.reduce((acc, p) => acc + (p.stats?.totalPaid ?? 0), 0)
    return buildTrendData(total)
  }, [partners])

  const refreshPartners = async () => {
    const resp = await api.get('/parceiros')
    setPartners(resp.data.data ?? [])
    setPyramidVersion((v) => v + 1)
  }

  const refreshSelectedPartner = async () => {
    if (!selectedPartner) return
    const resp = await api.get(`/parceiros/${selectedPartner.id}`)
    setSelectedPartner(resp.data.data)
  }

  const handleRecurrenceAction = async (dealId: string, action: 'end' | 'reopen') => {
    if (action === 'end' && !window.confirm('Encerrar a recorrência? Novas comissões mensais deixam de ser geradas para este deal.')) return
    try {
      await api.post(`/parceiros/deals/${dealId}/recurrence/${action}`, {})
      await refreshSelectedPartner()
      await refreshPartners()
      toast.success(action === 'end' ? 'Recorrência encerrada' : 'Recorrência reaberta')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao atualizar recorrência'))
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.comissao === '' || !Number.isFinite(Number(formData.comissao))) {
      toast.error('Informe a comissão acordada (%)')
      return
    }
    const payload = {
      ...formData,
      comissao: Number(formData.comissao),
      responsavelUserId: canAssignCommercial ? formData.responsavelUserId || null : undefined,
    }
    setSaving(true)
    try {
      if (formData.id) {
        await api.patch(`/parceiros/${formData.id}`, payload)
        toast.success('Parceiro atualizado com sucesso!')
      } else {
        const resp = await api.post('/parceiros', payload)
        const enviado = resp.data?.data?.confirmacaoEnviada
        toast.success(enviado
          ? 'Parceiro cadastrado. Enviamos o link para ele confirmar os dados e aceitar as regras.'
          : 'Parceiro cadastrado. O e-mail de confirmação não foi enviado.')
      }
      setShowFormModal(false)
      await refreshPartners()
      if (formData.id && selectedPartner?.id === formData.id) {
        await refreshSelectedPartner()
      }
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar parceiro'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este parceiro?')) return
    try {
      await api.delete(`/parceiros/${id}`)
      setPartners((prev) => prev.filter((p) => p.id !== id))
      setPyramidVersion((v) => v + 1)
      toast.success('Parceiro excluído com sucesso!')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao excluir parceiro'))
    }
  }

  const handleApprovePortalAccess = async (partner: PartnerView) => {
    if (!window.confirm(`Aprovar ${partner.nome} e enviar o convite do Portal do Parceiro por e-mail?`)) return
    try {
      const resp = await api.post(`/parceiros/${partner.id}/approve-portal`)
      await refreshPartners()
      if (selectedPartner?.id === partner.id) {
        const detailResp = await api.get(`/parceiros/${partner.id}`)
        setSelectedPartner(detailResp.data.data)
      }
      toast.success(resp.data?.data?.message ?? 'Acesso liberado e convite enviado')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao liberar acesso do parceiro'))
    }
  }

  const openForm = (partner?: PartnerView) => {
    if (partner) {
      setFormData({
        id: partner.id,
        nome: partner.nome,
        empresa: partner.empresa ?? '',
        email: partner.email ?? '',
        telefone: partner.telefone ?? '',
        comissao: Number(partner.comissao),
        cnpj: partner.cnpj ?? '',
        responsavel: partner.responsavel ?? '',
        telefoneContato: partner.telefoneContato ?? '',
        chavePix: partner.chavePix ?? '',
        indicadoPorId: partner.indicadoPorId ?? '',
        modoComissao: partner.modoComissao ?? 'RECORRENTE',
        responsavelUserId: partner.responsavelUser?.id ?? '',
      })
    } else {
      setFormData(EMPTY_FORM)
    }
    setShowFormModal(true)
  }

  const openDetail = async (partner: PartnerView) => {
    try {
      const resp = await api.get(`/parceiros/${partner.id}`)
      setSelectedPartner(resp.data.data)
    } catch {
      setSelectedPartner(partner)
    }
  }

  const handleUpdateCommissionStatus = async (commissionId: string, status: string) => {
    if (status === 'paga' && !window.confirm('Confirmar pagamento desta comissão?')) return
    try {
      await api.post(`/parceiros/comissoes/${commissionId}/status`, { status })
      if (selectedPartner) {
        const resp = await api.get(`/parceiros/${selectedPartner.id}`)
        setSelectedPartner(resp.data.data)
      }
      await refreshPartners()
      toast.success('Status da comissão atualizado!')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao atualizar comissão'))
    }
  }

  const handleCalculateCommission = async (partnerId: string, dealId: string) => {
    try {
      const resp = await api.post(`/parceiros/${partnerId}/calculate-commission`, { dealId })
      toast.success(resp.data.message || 'Comissões calculadas com sucesso')
      if (selectedPartner) {
        const detailResp = await api.get(`/parceiros/${selectedPartner.id}`)
        setSelectedPartner(detailResp.data.data)
      }
      await refreshPartners()
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao calcular comissões'))
    }
  }

  const handleAddPayment = async (data: {
    valor: number
    metodo: string
    notas: string
    dataPagamento?: string
    commissionIds?: string[]
  }) => {
    if (!selectedPartner) return
    try {
      await api.post(`/parceiros/${selectedPartner.id}/pagamentos`, data)
      const resp = await api.get(`/parceiros/${selectedPartner.id}`)
      setSelectedPartner(resp.data.data)
      await refreshPartners()
      toast.success('Pagamento registrado com sucesso!')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao registrar pagamento'))
    }
  }

  return {
    partners,
    loading,
    search,
    setSearch,
    showFormModal,
    setShowFormModal,
    formData,
    setFormData,
    saving,
    selectedPartner,
    setSelectedPartner,
    filtered,
    sparklineData,
    sparklineToPay,
    sparklinePaid,
    handleSave,
    handleDelete,
    handleApprovePortalAccess: canApprovePortalAccess ? handleApprovePortalAccess : undefined,
    openForm,
    openDetail,
    handleUpdateCommissionStatus,
    handleCalculateCommission,
    handleAddPayment,
    handleRecurrenceAction,
    pyramidVersion,
    refreshPartners,
    comerciais: canAssignCommercial ? comerciais : undefined,
  }
}
